export type TrendDirection = 'up' | 'down' | 'flat';

export type TrendInfo = {
  direction: TrendDirection;
  pctChange: number | null; // null when previous is 0 and current is also 0
  delta: number; // current - previous
};

const FLAT_THRESHOLD_PCT = 2; // changes below this magnitude are treated as flat

/**
 * Minimum previous-period value for percentage to be meaningful.
 * Below this threshold we show the absolute delta instead, because
 * percentages on tiny bases are misleading (1 → 3 = "+200%").
 */
const PCT_MIN_BASE = 10;

export const computeTrend = (current: number, previous: number): TrendInfo => {
  const delta = current - previous;

  if (previous === 0) {
    if (current === 0) return { direction: 'flat', pctChange: null, delta };

    return { direction: 'up', pctChange: null, delta };
  }

  const pctChange = previous >= PCT_MIN_BASE ? ((current - previous) / previous) * 100 : null;
  let direction: TrendDirection = 'flat';

  if (pctChange !== null && Math.abs(pctChange) >= FLAT_THRESHOLD_PCT) {
    direction = pctChange > 0 ? 'up' : 'down';
  } else if (pctChange === null && delta !== 0) {
    direction = delta > 0 ? 'up' : 'down';
  }

  return { direction, pctChange, delta };
};

export const formatTrend = (trend: TrendInfo): string => {
  if (trend.delta === 0) return '—';

  // When we have a meaningful percentage (previous ≥ 10), show it
  if (trend.pctChange !== null) {
    const sign = trend.pctChange > 0 ? '+' : '';

    return `${sign}${Math.round(trend.pctChange)}%`;
  }

  // Otherwise show absolute delta
  return trend.delta > 0 ? `+${trend.delta}` : `${trend.delta}`;
};

export const trendArrow = (direction: TrendDirection): string => {
  if (direction === 'up') return '↗';
  if (direction === 'down') return '↘';

  return '→';
};

export const trendColor = (direction: TrendDirection, isInverse = false): string => {
  // isInverse: when up means bad (e.g., rejections, errors)
  if (direction === 'flat') return 'text-gray-500';
  if (isInverse) {
    return direction === 'up' ? 'text-red-600' : 'text-green-600';
  }

  return direction === 'up' ? 'text-green-600' : 'text-amber-600';
};
